import { FC, useId } from "react";

export interface BarDatum {
	label: string;
	value: number;
}

interface Props {
	data: BarDatum[];
	ariaLabel: string;
	height?: number;
	color?: string;
	formatValue?: (n: number) => string;
	/** Unit label shown below the chart, e.g. "sessions", "users". */
	valueLabel?: string;
}

const BarChart: FC<Props> = ({
	data,
	ariaLabel,
	height = 220,
	color = "var(--color-primary)",
	formatValue = (n) => n.toLocaleString(),
	valueLabel
}) => {
	const id = useId();
	if (!data.length) {
		return (
			<div
				className="rounded-xl border border-dashed border-border bg-white p-6 text-sm text-muted-foreground"
				role="img"
				aria-label={`${ariaLabel} (no data)`}
			>
				No data for this range.
			</div>
		);
	}
	const max = Math.max(...data.map((d) => d.value), 1);

	return (
		<figure aria-labelledby={`${id}-title`}>
			<span id={`${id}-title`} className="sr-only">
				{ariaLabel}.{" "}
				{data.map((d) => `${d.label}: ${formatValue(d.value)}`).join("; ")}
			</span>
			<div
				role="img"
				aria-label={ariaLabel}
				className="space-y-1.5"
				style={{ minHeight: Math.min(height, data.length * 28) }}
			>
				{data.map((d) => {
					const pct = (d.value / max) * 100;
					return (
						<div
							key={d.label}
							className="grid grid-cols-[160px_1fr_72px] gap-3 items-center text-sm"
						>
							<span className="truncate text-foreground" title={d.label}>
								{d.label}
							</span>
							<div className="h-3 rounded-full bg-zinc-100 overflow-hidden">
								<div
									className="h-full rounded-full"
									style={{
										width: `${pct}%`,
										background: color
									}}
								/>
							</div>
							<span className="text-right tabular-nums text-foreground">
								{formatValue(d.value)}
							</span>
						</div>
					);
				})}
			</div>
			{valueLabel ? (
				<p className="text-[11px] text-muted-foreground text-right mt-1.5">
					{valueLabel}
				</p>
			) : null}
		</figure>
	);
};

export default BarChart;
